import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LineChart, Line, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend, Customized } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Button } from '../ui/button.tsx';
import { Checkbox } from '../ui/checkbox.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { Badge } from '../ui/badge.tsx';
import { FiDownload, FiLink, FiPlus } from 'react-icons/fi';
import { supabase } from '../../supabase';
import { useAuth } from '../../AuthContext';
import { getMaxBacktestYears, getMaxStartDate, getTierDisplayName } from '../../utils/tiers';
import { createShareLink } from '../../lib/api/shareLinks';
import FormulaEditor from '../strategy/FormulaEditor.tsx';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4998';
const MAX_POINTS = 1200;

const DEFAULT_ASSET_CLASS = 'stocks';
const ASSET_CLASS_OPTIONS = [
  { value: 'stocks', label: 'Stocks' },
  { value: 'etfs', label: 'ETFs' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'commodities', label: 'Commodities' }
];

const FALLBACK_PRESETS = [
  { id: 'price', name: 'Price', formula: 'price', category: 'market' },
  { id: 'pe_ratio', name: 'P/E Ratio', formula: 'price / eps.ttm(0)', category: 'valuation' },
  { id: 'revenue_yoy', name: 'Revenue YoY Growth', formula: 'revenue.ttm(0) / revenue.ttm(4) - 1', category: 'growth' },
  { id: 'fcf_margin', name: 'FCF Margin', formula: 'fcf.ttm(0) / revenue.ttm(0)', category: 'profitability' },
  { id: 'momentum_12m', name: '12M Momentum', formula: 'momentum(252)', category: 'technical' }
];

const SERIES_COLORS = ['#2563eb', '#0f766e', '#f97316', '#dc2626', '#16a34a', '#0ea5e9'];

const normalizeAssetClass = (value) => {
  const normalized = String(value || '').toLowerCase().trim();
  if (['stock', 'stocks', 'equity', 'equities'].includes(normalized)) return 'stocks';
  if (['etf', 'etfs'].includes(normalized)) return 'etfs';
  if (['crypto', 'cryptos', 'cryptoasset', 'cryptoassets'].includes(normalized)) return 'crypto';
  if (['commodity', 'commodities'].includes(normalized)) return 'commodities';
  return DEFAULT_ASSET_CLASS;
};

const normalizeSeriesTarget = (value) => {
  const normalized = String(value || '').toLowerCase().trim();
  if (['panel', 'below', 'bottom', 'sub', 'secondary_panel'].includes(normalized)) return 'panel';
  return 'main';
};

const normalizeSeriesAxis = (value) => {
  const normalized = String(value || '').toLowerCase().trim();
  if (['right', 'secondary', 'y2', 'secondary_axis'].includes(normalized)) return 'right';
  return 'left';
};

const normalizeSeriesConfig = (item) => {
  if (!item) return null;
  if (typeof item === 'string') {
    const trimmed = item.trim();
    return trimmed ? { formula: trimmed, target: 'main', axis: 'left' } : null;
  }
  if (typeof item !== 'object') return null;
  const formulaText = String(item.formula || item.expression || '').trim();
  if (!formulaText) return null;
  const targetHint = item.target ?? (item.panel ? 'panel' : null) ?? item.location;
  const axisHint = item.axis ?? item.y_axis ?? item.yAxis ?? item.yAxisId;
  return {
    formula: formulaText,
    name: item.name || item.label,
    target: normalizeSeriesTarget(targetHint),
    axis: normalizeSeriesAxis(axisHint)
  };
};

const buildDefaultDates = (billingTier) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = new Date(today);
  const startDate = getMaxStartDate(endDate, billingTier);
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    todayStr: today.toISOString().split('T')[0]
  };
};

const formatNumber = (value) => {
  if (!Number.isFinite(value)) return 'N/A';
  const abs = Math.abs(value);
  if (abs >= 1000) return value.toFixed(2);
  if (abs > 0 && abs < 0.01) return value.toFixed(6);
  return value.toFixed(4);
};

const formatPrice = (value) => {
  if (!Number.isFinite(value)) return 'N/A';
  return `$${value.toFixed(2)}`;
};

const formatPriceAxisTick = (value) => (Number.isFinite(value) ? `$${value.toFixed(0)}` : 'N/A');

const formatSeriesLabel = (formulaText) => {
  const trimmed = formulaText.trim();
  if (!trimmed) return 'Formula';
  if (trimmed.length <= 28) return trimmed;
  return `${trimmed.slice(0, 25)}...`;
};

const ChartWatermark = ({ width = 0, height = 0 }) => (
  <text
    x={Math.max(width - 8, 0)}
    y={Math.max(height - 8, 0)}
    textAnchor="end"
    fontSize="11"
    fill="rgba(15, 23, 42, 0.25)"
  >
    factorbench.com
  </text>
);

const ChartPage = () => {
  const location = useLocation();
  const { billingTier } = useAuth() || {};
  const tier = billingTier || 'free';
  const tierLabel = getTierDisplayName(tier);
  const maxYears = getMaxBacktestYears(tier);

  const defaults = useMemo(() => buildDefaultDates(tier), [tier]);

  const mainChartRef = useRef(null);
  const chartExportRef = useRef(null);
  const [assetClass, setAssetClass] = useState(DEFAULT_ASSET_CLASS);
  const [symbols, setSymbols] = useState([]);
  const [filteredSymbols, setFilteredSymbols] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [defaultSymbol, setDefaultSymbol] = useState('');
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [todayStr, setTodayStr] = useState(defaults.todayStr);
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);
  const [chartResult, setChartResult] = useState(null);
  const [chartResultId, setChartResultId] = useState(null);
  const [extraSeries, setExtraSeries] = useState([]);
  const [pendingSeriesConfigs, setPendingSeriesConfigs] = useState([]);
  const [hiddenSeries, setHiddenSeries] = useState({});
  const [addSeriesTarget, setAddSeriesTarget] = useState(null);
  const [seriesFormula, setSeriesFormula] = useState('');
  const [seriesValidation, setSeriesValidation] = useState(null);
  const [seriesError, setSeriesError] = useState(null);
  const [addingSeries, setAddingSeries] = useState(false);
  const [useSecondaryAxis, setUseSecondaryAxis] = useState(false);
  const [formula, setFormula] = useState(FALLBACK_PRESETS[0].formula);
  const [presets, setPresets] = useState(FALLBACK_PRESETS);
  const [selectedPresetId, setSelectedPresetId] = useState(FALLBACK_PRESETS[0].id);
  const [formulaValidation, setFormulaValidation] = useState(null);
  const [error, setError] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [shareFeedback, setShareFeedback] = useState(null);
  const [pendingSymbol, setPendingSymbol] = useState(null);

  const chartResultParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('result_id') || params.get('chart_result_id');
  }, [location.search]);

  const seriesKeyParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('series_key') || params.get('seriesKey');
  }, [location.search]);

  const maxStartDateStr = useMemo(() => {
    if (!endDate) return '';
    const parsed = new Date(endDate);
    if (Number.isNaN(parsed.getTime())) return '';
    return getMaxStartDate(parsed, tier).toISOString().split('T')[0];
  }, [endDate, tier]);

  useEffect(() => {
    const nextDefaults = buildDefaultDates(tier);
    setStartDate(nextDefaults.startDate);
    setEndDate(nextDefaults.endDate);
    setTodayStr(nextDefaults.todayStr);
  }, [tier]);

  useEffect(() => {
    if (!maxStartDateStr || !startDate) return;
    if (startDate < maxStartDateStr) {
      setStartDate(maxStartDateStr);
    }
  }, [maxStartDateStr, startDate]);

  useEffect(() => {
    if (!seriesKeyParam) return;
    try {
      const raw = localStorage.getItem(seriesKeyParam);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const seriesList = Array.isArray(parsed) ? parsed : parsed?.series;
      if (Array.isArray(seriesList) && seriesList.length > 0) {
        setPendingSeriesConfigs(seriesList);
      }
    } catch (err) {
      console.warn('Failed to load chart series config:', err);
    }
  }, [seriesKeyParam]);

  useEffect(() => {
    const loadPresets = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/formulas/presets`);
        if (!response.ok) return;
        const data = await response.json();
        const remote = Array.isArray(data.presets) ? data.presets : [];
        if (remote.length === 0) return;
        const combined = [...FALLBACK_PRESETS];
        const seen = new Set(combined.map((preset) => preset.id));
        remote.forEach((preset) => {
          if (!seen.has(preset.id)) {
            combined.push(preset);
            seen.add(preset.id);
          }
        });
        setPresets(combined);
      } catch (err) {
        console.error('Failed to load presets:', err);
      }
    };
    loadPresets();
  }, []);

  useEffect(() => {
    const fetchDefaultSymbol = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const response = await fetch(`${API_BASE}/api/user/settings`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
        if (!response.ok) return;
        const data = await response.json();
        const ticker = (data.settings?.debug_ticker || '').trim().toUpperCase();
        if (ticker) {
          setDefaultSymbol(ticker);
        }
      } catch (err) {
        console.error('Failed to fetch user settings:', err);
      }
    };
    fetchDefaultSymbol();
  }, []);

  useEffect(() => {
    const normalized = normalizeAssetClass(assetClass);
    const fetchSymbols = async () => {
      try {
        setLoadingSymbols(true);
        setError(null);
        const query = normalized ? `?asset_class=${encodeURIComponent(normalized)}` : '';
        const response = await fetch(`${API_BASE}/api/symbols${query}`);
        if (!response.ok) throw new Error('Failed to fetch symbols');
        const data = await response.json();
        const list = data.symbols || [];
        setSymbols(list);
        setFilteredSymbols(list);
        setSearchTerm('');
        setSelectedSymbol('');
      } catch (err) {
        setError(err.message || 'Failed to fetch symbols');
      } finally {
        setLoadingSymbols(false);
      }
    };
    fetchSymbols();
  }, [assetClass]);

  useEffect(() => {
    if (!defaultSymbol || selectedSymbol || symbols.length === 0) return;
    const match = symbols.find((symbol) => String(symbol.ticker || '').toUpperCase() === defaultSymbol);
    if (match) {
      setSelectedSymbol(match.ticker);
    }
  }, [defaultSymbol, selectedSymbol, symbols]);

  useEffect(() => {
    if (!pendingSymbol || symbols.length === 0) return;
    const match = symbols.find((symbol) => String(symbol.ticker || '').toUpperCase() === pendingSymbol);
    if (match) {
      setSelectedSymbol(match.ticker);
    }
    setPendingSymbol(null);
  }, [pendingSymbol, symbols]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredSymbols(symbols);
      return;
    }
    const lower = searchTerm.toLowerCase();
    const filtered = symbols.filter((symbol) => (
      (symbol.ticker?.toLowerCase() ?? '').includes(lower) ||
      (symbol.company_name?.toLowerCase() ?? '').includes(lower) ||
      (symbol.name?.toLowerCase() ?? '').includes(lower) ||
      (symbol.sector?.toLowerCase() ?? '').includes(lower) ||
      (symbol.exchange?.toLowerCase() ?? '').includes(lower)
    ));
    setFilteredSymbols(filtered);
  }, [searchTerm, symbols]);

  const baseChartPoints = useMemo(() => chartResult?.points || [], [chartResult]);
  const mainSeries = useMemo(() => extraSeries.filter((series) => series.target === 'main'), [extraSeries]);
  const panelSeries = useMemo(() => extraSeries.filter((series) => series.target === 'panel'), [extraSeries]);
  const resultFormula = chartResult?.formula || formula;
  const isPriceFormula = resultFormula.trim().toLowerCase() === 'price';
  const showPriceSeries = chartResult && !isPriceFormula;
  const isSeriesHidden = (dataKey) => Boolean(hiddenSeries[dataKey]);
  const hasVisibleRightSeries = mainSeries.some((series) => (
    series.yAxisId === 'right' && !isSeriesHidden(series.dataKey)
  ));
  const showRightAxis = (showPriceSeries && !isSeriesHidden('price')) || hasVisibleRightSeries;
  const rightAxisFormatter = showRightAxis && showPriceSeries && !isSeriesHidden('price') && !hasVisibleRightSeries
    ? formatPriceAxisTick
    : formatNumber;

  const legendItems = useMemo(() => {
    if (!chartResult) return [];
    const items = [
      {
        dataKey: 'value',
        label: formatSeriesLabel(resultFormula),
        color: '#2563eb'
      }
    ];
    mainSeries.forEach((series) => {
      items.push({
        dataKey: series.dataKey,
        label: series.label,
        color: series.color
      });
    });
    if (showPriceSeries) {
      items.push({
        dataKey: 'price',
        label: 'Price',
        color: '#94a3b8'
      });
    }
    return items;
  }, [chartResult, resultFormula, mainSeries, showPriceSeries]);

  const openAddSeries = (target) => {
    setAddSeriesTarget(target);
    setSeriesFormula('');
    setSeriesValidation(null);
    setSeriesError(null);
    setUseSecondaryAxis(false);
  };

  const closeAddSeries = useCallback(() => {
    setAddSeriesTarget(null);
    setSeriesFormula('');
    setSeriesValidation(null);
    setSeriesError(null);
    setUseSecondaryAxis(false);
  }, []);

  const applyChartResult = useCallback((resultPayload, resultId) => {
    if (!resultPayload) return;
    const nextSymbol = (resultPayload.symbol || '').toUpperCase();
    if (nextSymbol) {
      setPendingSymbol(nextSymbol);
    }
    if (resultPayload.asset_class) {
      setAssetClass(normalizeAssetClass(resultPayload.asset_class));
    }
    if (resultPayload.formula) {
      setFormula(resultPayload.formula);
    }
    if (resultPayload.start_date) {
      setStartDate(resultPayload.start_date);
    }
    if (resultPayload.end_date) {
      setEndDate(resultPayload.end_date);
    }
    setChartResult(resultPayload);
    setChartResultId(resultId || resultPayload.chart_result_id || resultPayload.id || null);
    setExtraSeries([]);
    setHiddenSeries({});
    closeAddSeries();
  }, [closeAddSeries]);

  useEffect(() => {
    if (!chartResultParam) return;
    const fetchChartResult = async () => {
      try {
        setLoadingChart(true);
        setError(null);
        setShareFeedback(null);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No active session');
        const response = await fetch(`${API_BASE}/api/chart-results/${chartResultParam}`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || 'Failed to load chart result');
        }
        const record = data.chart_result || data.result || data;
        const payload = {
          ...(record.chart_results || {}),
          symbol: record.symbol || record.chart_results?.symbol,
          asset_class: record.asset_class || record.chart_results?.asset_class,
          formula: record.formula || record.chart_results?.formula,
          start_date: record.start_date || record.chart_results?.start_date,
          end_date: record.end_date || record.chart_results?.end_date,
          name: record.name || record.chart_results?.name
        };
        applyChartResult(payload, record.id);
      } catch (err) {
        setError(err.message || 'Failed to load chart result');
      } finally {
        setLoadingChart(false);
      }
    };
    fetchChartResult();
  }, [chartResultParam, applyChartResult]);

  const mainChartData = useMemo(() => {
    const rows = baseChartPoints.map((pt) => ({
      date: pt.date,
      value: pt.value,
      price: pt.price
    }));
    if (mainSeries.length === 0) return rows;
    const indexByDate = new Map(rows.map((row, idx) => [row.date, idx]));
    mainSeries.forEach((series) => {
      series.points.forEach((pt) => {
        const idx = indexByDate.get(pt.date);
        if (idx !== undefined) {
          rows[idx][series.dataKey] = pt.value;
        }
      });
    });
    return rows;
  }, [baseChartPoints, mainSeries]);

  const panelCharts = useMemo(() => {
    if (!baseChartPoints.length) return [];
    return panelSeries.map((series) => {
      const rows = baseChartPoints.map((pt) => ({
        date: pt.date,
        [series.dataKey]: null
      }));
      const indexByDate = new Map(rows.map((row, idx) => [row.date, idx]));
      series.points.forEach((pt) => {
        const idx = indexByDate.get(pt.date);
        if (idx !== undefined) {
          rows[idx][series.dataKey] = pt.value;
        }
      });
      return { ...series, data: rows };
    });
  }, [baseChartPoints, panelSeries]);

  const chartRenderKey = chartResultId || `${chartResult?.symbol || 'chart'}-${mainChartData.length}-${mainSeries.length}`;

  const handlePresetChange = (presetId) => {
    setSelectedPresetId(presetId);
    if (presetId === 'custom') return;
    const preset = presets.find((item) => item.id === presetId);
    if (preset?.formula) {
      setFormula(preset.formula);
    }
  };

  const handleFormulaChange = (nextFormula) => {
    setFormula(nextFormula);
    const preset = presets.find((item) => item.id === selectedPresetId);
    if (preset && preset.formula.trim() !== nextFormula.trim()) {
      setSelectedPresetId('custom');
    }
  };

  const fetchChartSeries = useCallback(async (formulaText, context = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');
    const symbol = (context.symbol || context.ticker || selectedSymbol || '').toString().trim();
    if (!symbol) throw new Error('Symbol is required.');
    const assetClassValue = context.asset_class || context.assetClass || assetClass;
    const startDateValue = context.start_date || context.startDate || startDate;
    const endDateValue = context.end_date || context.endDate || endDate;
    const maxPointsValue = context.max_points || context.maxPoints || MAX_POINTS;

    const payload = {
      symbol: symbol.toUpperCase(),
      asset_class: normalizeAssetClass(assetClassValue),
      formula: formulaText.trim(),
      start_date: startDateValue,
      end_date: endDateValue,
      max_points: maxPointsValue
    };

    const response = await fetch(`${API_BASE}/api/charts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to run chart');
    }
    return data.result || data;
  }, [assetClass, endDate, selectedSymbol, startDate]);

  useEffect(() => {
    if (!chartResult || pendingSeriesConfigs.length === 0) return;
    let cancelled = false;

    const loadSeries = async () => {
      const normalizedSeries = pendingSeriesConfigs
        .map((series) => normalizeSeriesConfig(series))
        .filter(Boolean);
      if (normalizedSeries.length === 0) {
        setPendingSeriesConfigs([]);
        return;
      }
      const context = {
        symbol: chartResult.symbol,
        asset_class: chartResult.asset_class,
        start_date: chartResult.start_date,
        end_date: chartResult.end_date,
        max_points: MAX_POINTS
      };
      const errors = [];
      const results = await Promise.all(normalizedSeries.map(async (series) => {
        try {
          const resultPayload = await fetchChartSeries(series.formula, context);
          return { series, points: resultPayload?.points || [] };
        } catch (err) {
          errors.push(err);
          return null;
        }
      }));
      if (cancelled) return;
      const validResults = results.filter(Boolean);
      if (validResults.length > 0) {
        setExtraSeries((prev) => {
          const baseIndex = prev.length;
          const stamp = Date.now();
          const next = validResults.map((entry, idx) => {
            const target = entry.series.target === 'panel' ? 'panel' : 'main';
            const yAxisId = target === 'main' && entry.series.axis === 'right' ? 'right' : 'left';
            return {
              id: `series_${stamp}_${idx}`,
              dataKey: `series_${stamp}_${idx}`,
              formula: entry.series.formula,
              label: entry.series.name || formatSeriesLabel(entry.series.formula),
              color: SERIES_COLORS[(baseIndex + idx + 1) % SERIES_COLORS.length],
              target,
              yAxisId,
              points: entry.points
            };
          });
          return [...prev, ...next];
        });
      }
      if (errors.length > 0 && !cancelled) {
        setError(errors[0]?.message || 'Failed to load additional chart series.');
      }
      if (!cancelled) {
        setPendingSeriesConfigs([]);
      }
    };

    loadSeries();

    return () => {
      cancelled = true;
    };
  }, [chartResult, fetchChartSeries, pendingSeriesConfigs]);

  const handleAddSeries = async () => {
    if (!chartResult) {
      setSeriesError('Run a base chart first.');
      return;
    }
    if (!seriesFormula.trim()) {
      setSeriesError('Please enter a formula.');
      return;
    }
    if (seriesValidation && !seriesValidation.valid) {
      setSeriesError('Fix formula errors before adding.');
      return;
    }
    try {
      setAddingSeries(true);
      setSeriesError(null);
      const resultPayload = await fetchChartSeries(seriesFormula);
      const dataKey = `series_${Date.now()}`;
      const color = SERIES_COLORS[(extraSeries.length + 1) % SERIES_COLORS.length];
      const yAxisId = addSeriesTarget === 'main' && useSecondaryAxis ? 'right' : 'left';
      const nextSeries = {
        id: dataKey,
        dataKey,
        formula: seriesFormula.trim(),
        label: formatSeriesLabel(seriesFormula),
        color,
        target: addSeriesTarget,
        yAxisId,
        points: resultPayload?.points || []
      };
      setExtraSeries((prev) => [...prev, nextSeries]);
      closeAddSeries();
    } catch (err) {
      setSeriesError(err.message || 'Failed to add series.');
    } finally {
      setAddingSeries(false);
    }
  };

  const handleDownloadChart = () => {
    const container = chartExportRef.current || mainChartRef.current;
    if (!container) return;
    const svgs = Array.from(container.querySelectorAll('.recharts-wrapper svg'));
    if (svgs.length === 0) return;
    const svgEntries = svgs
      .map((svg) => {
        const bbox = svg.getBoundingClientRect();
        return {
          svg,
          width: bbox.width || 0,
          height: bbox.height || 0
        };
      })
      .filter((entry) => entry.width > 0 && entry.height > 0);
    if (svgEntries.length === 0) return;

    const spacing = 24;
    const maxWidth = Math.max(...svgEntries.map((entry) => entry.width));
    const totalHeight = svgEntries.reduce(
      (acc, entry, idx) => acc + entry.height + (idx > 0 ? spacing : 0),
      0
    );
    if (!maxWidth || !totalHeight) return;

    const scale = window.devicePixelRatio || 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(maxWidth * scale);
    canvas.height = Math.ceil(totalHeight * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(scale, scale);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, maxWidth, totalHeight);

    const serializer = new XMLSerializer();
    const positions = [];
    let currentY = 0;
    svgEntries.forEach((entry, idx) => {
      positions[idx] = currentY;
      currentY += entry.height + spacing;
    });

    let loaded = 0;
    const finalize = () => {
      const pngUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = pngUrl;
      link.download = `chart-${chartResult?.symbol || 'factorbench'}.png`;
      link.click();
    };

    svgEntries.forEach((entry, idx) => {
      const svgText = serializer.serializeToString(entry.svg);
      const svgBlob = new Blob([`<?xml version="1.0" encoding="UTF-8"?>${svgText}`], {
        type: 'image/svg+xml;charset=utf-8'
      });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      const finalizeLoad = () => {
        loaded += 1;
        if (loaded === svgEntries.length) {
          finalize();
        }
      };
      img.onload = () => {
        const x = (maxWidth - entry.width) / 2;
        ctx.drawImage(img, x, positions[idx], entry.width, entry.height);
        URL.revokeObjectURL(url);
        finalizeLoad();
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        finalizeLoad();
      };
      img.src = url;
    });
  };

  const handleRunChart = async () => {
    if (!selectedSymbol) {
      setError('Please select an asset.');
      return;
    }
    if (!formula.trim()) {
      setError('Please enter a formula.');
      return;
    }
    if (formulaValidation && !formulaValidation.valid) {
      setError('Please fix the formula validation errors before running.');
      return;
    }
    if (!startDate || !endDate) {
      setError('Please select a start and end date.');
      return;
    }
    try {
      setLoadingChart(true);
      setError(null);
      setShareFeedback(null);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const payload = {
        symbol: selectedSymbol,
        asset_class: normalizeAssetClass(assetClass),
        formula: formula.trim(),
        start_date: startDate,
        end_date: endDate,
        max_points: MAX_POINTS
      };

      const response = await fetch(`${API_BASE}/api/charts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to run chart');
      }
      const resultPayload = data.result || data;
      setChartResult(resultPayload);
      setChartResultId(resultPayload?.chart_result_id || null);
      setExtraSeries([]);
      setHiddenSeries({});
      closeAddSeries();
    } catch (err) {
      setError(err.message || 'Failed to run chart');
    } finally {
      setLoadingChart(false);
    }
  };

  const handleShare = async () => {
    if (!chartResultId) return;
    setShareFeedback(null);
    try {
      setSharing(true);
      const shareId = await createShareLink('chart_result', chartResultId);
      const shareUrl = `${window.location.origin}/share/${shareId}`;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        window.prompt('Copy share link:', shareUrl);
      }
      setShareFeedback({ message: 'Share link copied to clipboard.' });
    } catch (err) {
      setShareFeedback({
        message: err.message || 'Failed to create share link',
        isError: true
      });
    } finally {
      setSharing(false);
    }
  };

  const toggleSeriesVisibility = (dataKey) => {
    setHiddenSeries((prev) => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  };

  const renderLegend = () => (
    <div className="flex flex-wrap items-center gap-2 px-1 py-2 text-xs text-gray-600">
      {legendItems.map((item) => {
        const hidden = isSeriesHidden(item.dataKey);
        return (
          <button
            key={item.dataKey}
            type="button"
            onClick={() => toggleSeriesVisibility(item.dataKey)}
            className={`flex items-center gap-2 rounded-full border px-3 py-1 transition ${
              hidden ? 'border-gray-200 text-gray-400 line-through' : 'border-gray-300 text-gray-700 hover:border-gray-400'
            }`}
            aria-pressed={!hidden}
          >
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: item.color, opacity: hidden ? 0.3 : 1 }}
            />
            {item.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="chart-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Chart</h1>
          <p className="text-gray-600">
            Plot any formula over time, with price as a built-in comparison.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {chartResult && (
            <Button
              variant="outline"
              onClick={handleDownloadChart}
              className="flex items-center gap-2 px-4 py-2"
            >
              <FiDownload />
              Download
            </Button>
          )}
          {chartResultId && (
            <Button
              variant="outline"
              onClick={handleShare}
              disabled={sharing}
              className="flex items-center gap-2 px-4 py-2"
            >
              <FiLink />
              {sharing ? 'Sharing...' : 'Share'}
            </Button>
          )}
          <Button
            onClick={handleRunChart}
            disabled={loadingChart || !selectedSymbol}
            className="px-5 py-2"
          >
            {loadingChart ? 'Running...' : 'Run Chart'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {shareFeedback && (
        <div className={`mb-6 rounded-lg border p-3 text-sm ${shareFeedback.isError ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
          {shareFeedback.message}
        </div>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Chart Inputs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="chart-asset-class">Asset Class</Label>
              <select
                id="chart-asset-class"
                value={assetClass}
                onChange={(e) => setAssetClass(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              >
                {ASSET_CLASS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="chart-preset">Formula Preset</Label>
              <select
                id="chart-preset"
                value={selectedPresetId}
                onChange={(e) => handlePresetChange(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              >
                {presets.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.name} {preset.category ? `(${preset.category})` : ''}
                  </option>
                ))}
                <option value="custom">Custom</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="chart-search">Find Asset</Label>
              <Input
                id="chart-search"
                placeholder="Search ticker, name, or sector"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loadingSymbols}
              />
              <select
                value={selectedSymbol}
                onChange={(e) => setSelectedSymbol(e.target.value)}
                disabled={loadingSymbols || filteredSymbols.length === 0}
                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              >
                <option value="">{loadingSymbols ? 'Loading assets...' : 'Select an asset'}</option>
                {filteredSymbols.slice(0, 500).map((symbol) => {
                  const name = symbol.company_name || symbol.name || symbol.ticker;
                  return (
                    <option key={symbol.ticker} value={symbol.ticker}>
                      {symbol.ticker}{name ? ` - ${name}` : ''}
                    </option>
                  );
                })}
              </select>
              <div className="text-xs text-gray-500">
                {filteredSymbols.length} matching assets
              </div>
            </div>
            <div className="space-y-2">
              <Label>Timeframe</Label>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="chart-start" className="text-xs text-gray-600">Start</Label>
                  <Input
                    id="chart-start"
                    type="date"
                    value={startDate}
                    min={maxStartDateStr}
                    max={endDate || todayStr}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="chart-end" className="text-xs text-gray-600">End</Label>
                  <Input
                    id="chart-end"
                    type="date"
                    value={endDate}
                    min={startDate || maxStartDateStr}
                    max={todayStr}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Your {tierLabel} plan allows up to {maxYears} years. Earliest allowed start date: {maxStartDateStr || 'N/A'}.
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <Label>Formula</Label>
            <FormulaEditor
              value={formula}
              onChange={handleFormulaChange}
              onValidate={setFormulaValidation}
              apiBaseUrl={API_BASE}
            />
          </div>
        </CardContent>
      </Card>

      {chartResult && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle>{chartResult.symbol} Formula Chart</CardTitle>
                <div className="mt-1 text-sm text-gray-600">
                  {chartResult.start_date} to {chartResult.end_date} · {chartResult.total_points ?? mainChartData.length} points
                </div>
              </div>
              <Badge className="bg-blue-100 text-blue-700">{resultFormula}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6" ref={chartExportRef}>
              <div className="relative" ref={mainChartRef}>
                <div className="w-full" style={{ height: 384 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={mainChartData} key={chartRenderKey}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis yAxisId="left" tickFormatter={formatNumber} />
                    {showRightAxis && (
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tickFormatter={rightAxisFormatter}
                      />
                    )}
                    <Tooltip
                      formatter={(value, name) => {
                        if (!Number.isFinite(value)) return ['N/A', name];
                        return name === 'Price' ? [formatPrice(value), name] : [formatNumber(value), name];
                      }}
                    />
                    <Legend content={renderLegend} />
                    {!isSeriesHidden('value') && (
                      <Line
                        type="monotone"
                        dataKey="value"
                        name={formatSeriesLabel(resultFormula)}
                        stroke="#2563eb"
                        strokeWidth={2}
                        dot={false}
                        yAxisId="left"
                        isAnimationActive={false}
                      />
                    )}
                    {mainSeries.map((series) => {
                      if (isSeriesHidden(series.dataKey)) return null;
                      return (
                        <Line
                          key={series.id}
                          type="monotone"
                          dataKey={series.dataKey}
                          name={series.label}
                          stroke={series.color}
                          strokeWidth={2}
                          dot={false}
                          yAxisId={series.yAxisId === 'right' ? 'right' : 'left'}
                          connectNulls
                          isAnimationActive={false}
                        />
                      );
                    })}
                    {showPriceSeries && !isSeriesHidden('price') && (
                      <Line
                        type="monotone"
                        dataKey="price"
                        name="Price"
                        stroke="#94a3b8"
                        strokeWidth={2}
                        dot={false}
                        yAxisId="right"
                        isAnimationActive={false}
                      />
                    )}
                    <Customized component={ChartWatermark} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openAddSeries('main')}
                className="absolute right-3 top-3 h-9 w-9 rounded-full p-0"
                disabled={!chartResult}
                aria-label="Add line to main chart"
              >
                <FiPlus />
              </Button>
              </div>

              <div className="flex items-center justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openAddSeries('panel')}
                  className="h-9 w-9 rounded-full p-0"
                  disabled={!chartResult}
                  aria-label="Add panel chart"
                >
                  <FiPlus />
                </Button>
              </div>

              {addSeriesTarget && (
                <Card className="border border-blue-100 bg-blue-50/40">
                  <CardHeader>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <CardTitle className="text-base">
                        {addSeriesTarget === 'main' ? 'Add Line to Main Chart' : 'Add Panel Chart'}
                      </CardTitle>
                      {addSeriesTarget === 'main' && (
                        <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
                          <Checkbox
                            checked={useSecondaryAxis}
                            onCheckedChange={setUseSecondaryAxis}
                          />
                          Use secondary y-axis
                        </label>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {seriesError && (
                      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        {seriesError}
                      </div>
                    )}
                    <FormulaEditor
                      value={seriesFormula}
                      onChange={setSeriesFormula}
                      onValidate={setSeriesValidation}
                      apiBaseUrl={API_BASE}
                    />
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={handleAddSeries}
                        disabled={addingSeries}
                      >
                        {addingSeries ? 'Adding...' : 'Add Formula'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={closeAddSeries}
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {panelCharts.map((series) => (
                <Card key={series.id}>
                  <CardHeader>
                    <CardTitle className="text-base">{series.label}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="w-full" style={{ height: 220 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={series.data} key={`${chartRenderKey}-${series.id}`}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis tickFormatter={formatNumber} />
                          <Tooltip formatter={(value) => [formatNumber(value), series.label]} />
                          <Line
                            type="monotone"
                            dataKey={series.dataKey}
                            name={series.label}
                            stroke={series.color}
                            strokeWidth={2}
                            dot={false}
                            connectNulls
                            isAnimationActive={false}
                          />
                          <Customized component={ChartWatermark} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ChartPage;
